import { useState, useEffect } from 'react';
import {
  Box,
  Text,
  Badge,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { CheckCircle, User } from 'lucide-react';
import { Issue, MeetingIssue } from '../../types';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { COLLECTION_NAMES } from '../../types';

interface MeetingIssueCardProps {
  meetingIssue: MeetingIssue & { issue?: Issue };
  onResolve: (issueId: string, issueName: string) => void;
  isPresenter?: boolean;
}

export function MeetingIssueCard({
  meetingIssue,
  onResolve,
  isPresenter = true,
}: MeetingIssueCardProps) {
  const { issue } = meetingIssue;
  const [rockName, setRockName] = useState<string | null>(null);
  const [metricName, setMetricName] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const resolvedBg = useColorModeValue('green.50', 'green.900');

  useEffect(() => {
    const fetchLinkedData = async () => {
      if (issue?.relatedRockId) {
        try {
          const rockDoc = await getDoc(doc(db, COLLECTION_NAMES.ROCKS, issue.relatedRockId));
          if (rockDoc.exists()) {
            setRockName(rockDoc.data().rock);
          }
        } catch (error) {
          console.error('Error fetching rock:', error);
        }
      }

      if (issue?.relatedMetricId) {
        try {
          const metricDoc = await getDoc(doc(db, COLLECTION_NAMES.METRICS, issue.relatedMetricId));
          if (metricDoc.exists()) {
            const data = metricDoc.data();
            setMetricName(data.metricName || 'Unknown Metric');
          }
        } catch (error) {
          console.error('Error fetching metric:', error);
        }
      }
    };

    if (issue) {
      fetchLinkedData();
    }
  }, [issue?.relatedRockId, issue?.relatedMetricId]);

  if (!issue) return null;

  const showWeeksBadge = meetingIssue.weeksUnresolvedAtAdd >= 1;
  const isResolved = meetingIssue.resolvedInThisMeeting;

  return (
    <Box
      bg={bgColor}
      borderWidth={1}
      borderColor={borderColor}
      borderRadius="md"
      p={4}
      position="relative"
      opacity={isResolved ? 0.6 : 1}
    >
      {showWeeksBadge && !isResolved && (
        <Badge
          position="absolute"
          top={3}
          right={3}
          colorScheme="red"
          fontSize="xs"
          fontWeight="bold"
        >
          Week {meetingIssue.weeksUnresolvedAtAdd}
        </Badge>
      )}

      {isResolved && (
        <Badge
          position="absolute"
          top={3}
          right={3}
          colorScheme="green"
          fontSize="xs"
          fontWeight="bold"
        >
          <Icon as={CheckCircle} w={3} h={3} mr={1} />
          Resolved
        </Badge>
      )}

      <VStack align="stretch" spacing={3}>
        <Text fontSize="lg" fontWeight="semibold" pr={20}>
          {issue.name}
        </Text>

        <Text
          color="gray.700"
          noOfLines={expanded ? undefined : 2}
        >
          {issue.description}
        </Text>
        {issue.description.length > 100 && (
          <Button
            size="sm"
            variant="link"
            colorScheme="blue"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        )}

        <HStack spacing={2} flexWrap="wrap">
          {rockName && (
            <Badge colorScheme="blue">Rock: {rockName}</Badge>
          )}
          {metricName && (
            <Badge colorScheme="green">Metric: {metricName}</Badge>
          )}
          <Badge
            colorScheme={
              issue.priority === 'high'
                ? 'red'
                : issue.priority === 'medium'
                ? 'yellow'
                : 'gray'
            }
          >
            {issue.priority.charAt(0).toUpperCase() + issue.priority.slice(1)}
          </Badge>
        </HStack>

        {isResolved && meetingIssue.resolutionNotes && (
          <Box bg={resolvedBg} borderWidth={1} borderColor="green.200" borderRadius="md" p={3}>
            <Text fontSize="sm" fontWeight="medium" color="green.900" mb={1}>
              Resolution Notes:
            </Text>
            <Text fontSize="sm" color="green.800">
              {meetingIssue.resolutionNotes}
            </Text>
          </Box>
        )}

      </VStack>
    </Box>
  );
}

